// src/components/ui/Table/Pagination.tsx
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCompactNumber } from '@/utils/Formatters';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useResponsive } from '@/hooks/useResponsive';
import styles from './Pagination.module.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  pageSize?: number;
  itemName?: string;
  disabled?: boolean;
  // Параметры для адаптивности
  isMobile?: boolean;
  isTablet?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages: propsTotal,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSize = 10,
  itemName = 'items',
  disabled = false,
  isMobile: propsMobile,
  isTablet: propsTablet
}) => {
  const { t } = useTranslation(['ui', 'common']);
  const [goToPage, setGoToPage] = useState('');
  const { isMobile: hookMobile, isTablet: hookTablet } = useResponsive();
  
  // Используем props или определяем через хук
  const isMobile = propsMobile !== undefined ? propsMobile : hookMobile;
  const isTablet = propsTablet !== undefined ? propsTablet : hookTablet;
  
  // Добавляем проверку, чтобы totalPages был минимум 1
  const totalPages = Math.max(1, propsTotal);
  
  // Вычисляем индексы начала и конца для текущей страницы
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  // Определяем режим отображения на основе размера устройства
  const isCompactMode = isMobile || (isTablet && typeof window !== 'undefined' && window.innerWidth < 768);

  // Создаем массив номеров страниц с эллипсисами для большого количества страниц
  const pageNumbers = useMemo(() => {
    // Для мобильных устройств используем более компактное отображение
    if (isCompactMode) {
      // Для мобильных показываем меньше страниц
      const result: (number | string)[] = [];
      
      // Всегда показываем первую страницу
      result.push(1);
      
      // Если текущая страница далеко от начала, показываем эллипсис
      if (currentPage > 2) {
        result.push('...');
      }
      
      // Показываем текущую страницу, если она не 1
      if (currentPage > 1 && currentPage < totalPages) {
        result.push(currentPage);
      }
      
      // Если текущая страница далеко от конца, показываем эллипсис
      if (currentPage < totalPages - 1) {
        result.push('...');
      }
      
      // Всегда показываем последнюю страницу, если она отличается от первой
      if (totalPages > 1) {
        result.push(totalPages);
      }
      
      return result;
    } else {
      // Стандартная логика для больших экранов
      if (totalPages <= 7) {
        // Если страниц мало, показываем все номера
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      // Для большого количества страниц используем умную навигацию
      const result: (number | string)[] = [];
      
      // Всегда показываем первую страницу
      result.push(1);
      
      // Добавляем эллипсис, если текущая страница далеко от начала
      if (currentPage > 3) {
        result.push('...');
      }
      
      // Определяем диапазон страниц вокруг текущей
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Корректируем, если мы близко к началу или концу
      if (currentPage <= 3) {
        end = Math.min(5, totalPages - 1);
      } else if (currentPage >= totalPages - 2) {
        start = Math.max(totalPages - 4, 2);
      }
      
      // Добавляем номера страниц в диапазоне
      for (let i = start; i <= end; i++) {
        result.push(i);
      }
      
      // Добавляем эллипсис, если текущая страница далеко от конца
      if (currentPage < totalPages - 2) {
        result.push('...');
      }
      
      // Всегда показываем последнюю страницу, если она отличается от первой
      if (totalPages > 1) {
        result.push(totalPages);
      }
      
      return result;
    }
  }, [currentPage, totalPages, isCompactMode]);

  // Форматируем большие числа в компактный вид
  const formattedTotal = totalItems > 1000 
    ? formatCompactNumber(totalItems, 1) 
    : totalItems.toString();

  // Доступные размеры страницы (для мобильных меньше вариантов)
  const pageSizeOptions = isCompactMode ? [10, 20, 50] : [10, 20, 50, 100];
  
  // ИСПРАВЛЕНО: Улучшенные обработчики для навигации
  // Обработчик для перехода на предыдущую страницу
  const handlePrevPage = useCallback(() => {
    if (currentPage <= 1) return; // Не переходим, если уже на первой странице
    
    console.log(`Переход на предыдущую страницу: ${currentPage - 1}`);
    onPageChange(currentPage - 1);
  }, [currentPage, onPageChange]);

  // Обработчик для перехода на следующую страницу
  const handleNextPage = useCallback(() => {
    if (currentPage >= totalPages) return; // Не переходим, если уже на последней странице
    
    console.log(`Переход на следующую страницу: ${currentPage + 1}`);
    onPageChange(currentPage + 1);
  }, [currentPage, totalPages, onPageChange]);

  // Обработчик для перехода на конкретную страницу
  const handlePageClick = useCallback((page: number) => {
    if (page === currentPage) return; // Не переходим, если это текущая страница
    
    console.log(`Переход на страницу: ${page}`);
    onPageChange(page);
  }, [currentPage, onPageChange]);
  
  // Обработчик перехода на указанную страницу через ввод
  const handleGoToPage = useCallback(() => {
    const page = parseInt(goToPage, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      console.log(`Переход на введённую страницу: ${page}`);
      onPageChange(page);
      setGoToPage('');
    }
  }, [goToPage, totalPages, onPageChange]);

  // Обработчик для поля ввода номера страницы
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  }, [handleGoToPage]);

  // Обработчик изменения размера страницы
  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!onPageSizeChange) return;
    
    const newSize = Number(e.target.value);
    console.log(`Изменение размера страницы на: ${newSize}`);
    onPageSizeChange(newSize);
  }, [onPageSizeChange]);

  // ИСПРАВЛЕНО: Добавлены дополнительные проверки и отладочный вывод
  console.log('Pagination render:', { 
    currentPage, 
    totalPages, 
    totalItems, 
    pageSize,
    disabled 
  });

  return (
    <div className={`${styles.container} ${isCompactMode ? styles.compactContainer : ''}`}>
      {/* Мобильная версия */}
      {isCompactMode ? (
        <div className={styles.mobileContainer}>
          <button
            onClick={handlePrevPage}
            disabled={disabled || currentPage === 1}
            className={`${styles.arrowButton} ${styles.arrowButtonCompact} ${disabled || currentPage === 1 ? styles.disabled : ''}`}
            aria-label={t('table.pagination.previous', { defaultValue: "Предыдущая" })}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          <div className={`${styles.paginationNav} ${styles.paginationNavCompact}`}>
            {pageNumbers.map((item, index) => (
              typeof item === 'number' ? (
                <button
                  key={index}
                  onClick={() => handlePageClick(item)}
                  disabled={disabled || item === currentPage}
                  className={`
                    ${styles.pageButton} 
                    ${styles.pageButtonCompact} 
                    ${item === currentPage ? styles.activePageButton : ''} 
                    ${disabled ? styles.disabled : ''}
                  `}
                  aria-current={item === currentPage ? 'page' : undefined}
                >
                  {item}
                </button>
              ) : (
                <span key={index} className={styles.ellipsis}>
                  {item}
                </span>
              )
            ))}
          </div>
          
          <button
            onClick={handleNextPage}
            disabled={disabled || currentPage === totalPages}
            className={`${styles.arrowButton} ${styles.arrowButtonCompact} ${disabled || currentPage === totalPages ? styles.disabled : ''}`}
            aria-label={t('table.pagination.next', { defaultValue: "Следующая" })}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        /* Десктопная версия */
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          {/* Информация о количестве элементов */}
          <div className={styles.paginationInfoContainer}>
            <p className={styles.paginationInfo}>
              {t('table.pagination.showing', { defaultValue: "Показано" })} {startIndex + 1} {t('table.pagination.to', { defaultValue: "по" })} {Math.min(endIndex, totalItems)} {t('table.pagination.of', { defaultValue: "из" })} {formattedTotal} {itemName}
            </p>
            
            {/* Выбор размера страницы, если передана функция onPageSizeChange */}
            {onPageSizeChange && (
              <div className={styles.pageSizeSelector}>
                <span className={styles.pageSizeLabel}>{t('table.pagination.perPage', { defaultValue: "на странице" })}</span>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className={`${styles.pageSizeSelect} ${disabled ? styles.disabled : ''}`}
                  disabled={disabled}
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Кнопки страниц - центрированные */}
          <div className={`${styles.paginationNav} ${styles.paginationNavRegular}`}>
            {/* Кнопка "Предыдущая" */}
            <button
              onClick={handlePrevPage}
              disabled={disabled || currentPage === 1}
              className={`${styles.arrowButton} ${disabled || currentPage === 1 ? styles.disabled : ''}`}
              aria-label={t('table.pagination.previous', { defaultValue: "Предыдущая" })}
            >
              <span className="sr-only">{t('table.pagination.previous', { defaultValue: "Предыдущая" })}</span>
              <ChevronLeft className="h-5 w-5" />
            </button>

            {/* Номера страниц с умной навигацией */}
            {pageNumbers.map((item, index) => (
              typeof item === 'number' ? (
                <button
                  key={index}
                  onClick={() => handlePageClick(item)}
                  disabled={disabled || item === currentPage}
                  className={`
                    ${styles.pageButton} 
                    ${item === currentPage ? styles.activePageButton : ''} 
                    ${disabled ? styles.disabled : ''}
                  `}
                  aria-current={item === currentPage ? 'page' : undefined}
                >
                  {item}
                </button>
              ) : (
                <span key={index} className={styles.ellipsis}>
                  {item}
                </span>
              )
            ))}

            {/* Кнопка "Следующая" */}
            <button
              onClick={handleNextPage}
              disabled={disabled || currentPage === totalPages || totalPages === 0}
              className={`${styles.arrowButton} ${disabled || currentPage === totalPages || totalPages === 0 ? styles.disabled : ''}`}
              aria-label={t('table.pagination.next', { defaultValue: "Следующая" })}
            >
              <span className="sr-only">{t('table.pagination.next', { defaultValue: "Следующая" })}</span>
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          {/* Переход к конкретной странице */}
          <div className={styles.goToPageContainer}>
            <input
              type="text"
              value={goToPage}
              onChange={(e) => setGoToPage(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`${styles.goToPageInput} ${disabled ? styles.disabled : ''}`}
              placeholder={t('table.pagination.goToPage', { defaultValue: "Страница" })}
              disabled={disabled}
            />
            <button
              onClick={handleGoToPage}
              disabled={disabled}
              className={`${styles.goToPageButton} ${disabled ? styles.disabled : ''}`}
            >
              {t('table.pagination.go', { defaultValue: "Перейти" })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Pagination);